import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetch, { CachePolicies } from 'use-http';
import { Alert, Stack, Typography, Paper, Box, Button as MuiButton } from '@mui/material';
import { MenuBook } from '@mui/icons-material';


export const Home = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState<string[]>([]);
    
    const { get: getBatches, loading: batchesLoading } = useFetch('/api/batches', {
        cachePolicy: CachePolicies.NO_CACHE,
    });

    // Load available batches on mount
    useEffect(() => {
        loadBatches();
    }, []);
    
    const loadBatches = async () => {
        const data = await getBatches();
        if (data && data.batches) {
            setBatches(data.batches);
        }
    };
    
    return (
        <Box sx={{ p: 3 }}>
            <h1>Paper Figure QA Annotation Platform</h1>
            
            <Box sx={{ 
                display: 'flex', 
                gap: 4, 
                mt: 2,
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'stretch'
            }}>
                {/* Left Column - Task Overview */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper elevation={2} sx={{ p: 4, bgcolor: 'background.paper', height: '100%' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Task Overview
                        </Typography>
                        
                        <Typography variant="body1" paragraph>
                            You will be shown a series of queries about a scientific paper. For each query, we provide a model response 
                            to the query and its referenced associated figures and tables, and we ask you to assess several qualities of 
                            the response and the figures and tables.
                        </Typography>
                        
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                            Three Types of Queries
                        </Typography>
                        
                        <Stack spacing={2.5} sx={{ mt: 2 }}>
                            {/* General Queries */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    1. General Queries
                                </Typography>
                                <Typography variant="body2" paragraph sx={{ mt: 1, ml: 2 }}>
                                    These are broad questions about the paper's main findings or overall content (e.g., "show me the main results?"). 
                                    For general queries, we will ask you to evaluate whether each referenced figure or table directly supports 
                                    one of the paper's primary conclusions.
                                </Typography>
                            </Box>
                            
                            {/* Section-Specific Queries */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    2. Section- or Method-Specific Queries
                                </Typography>
                                <Typography variant="body2" paragraph sx={{ mt: 1, ml: 2 }}>
                                    These queries ask about a particular section or scoped item within the paper (e.g., "What does the pipeline look like?" 
                                    or questions about specific methods). For section- or method-specific queries, we will ask you to evaluate whether the 
                                    referenced figures and tables are directly scoped to the sections or methods the query asked about. 
                                    The multimodal content should be from within that specific section, not from other parts of the paper.
                                </Typography>
                            </Box>
                            
                            {/* Figure-Specific Queries */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    3. Figure-Specific Queries
                                </Typography>
                                <Typography variant="body2" paragraph sx={{ mt: 1, ml: 2 }}>
                                    These queries explicitly ask about a specific figure or table by name (e.g., "describe Figure 2" or 
                                    "what does Table 1 show?"). For figure-specific queries, we will ask you a comprehensive set of questions 
                                    about the quality and completeness of the figure description, including: accuracy of described elements, 
                                    coverage of multi-part figures, whether natural visual order is followed, how graphs and trends are described, 
                                    coverage of important visual components, handling of visual semantics (colors, shapes, etc.), and whether 
                                    unnecessary visual details are included.
                                </Typography>
                            </Box>
                        </Stack>
                        
                        <Typography variant="h6" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
                            Annotation Process
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                            • Answer all rubric questions for each query before moving on to the next<br />
                            • Navigate through queries using the Previous/Next buttons at the bottom<br />
                            • If something is unusual about the response or not captured in the rubric questions, please document 
                            these observations in the "Additional Notes" space provided<br />
                            • You can flag a query/response pair if it contains completely unrelated or hallucinated information<br />
                            • Submit your annotations only after completing all queries in the batch
                        </Typography>
                    </Paper>
                </Box>
                
                {/* Right Column - Tutorial and Batches */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack spacing={4}>
                        {/* Tutorial & Resources Section */}
                        <Paper elevation={3} sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                            <Stack spacing={2}>
                                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MenuBook />
                                    📚 New to Annotating?
                                </Typography>
                                <Typography variant="body1">
                                    Start with our tutorial to see complete annotation examples with detailed explanations.
                                </Typography>
                                <MuiButton
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    startIcon={<MenuBook />}
                                    onClick={() => navigate('/tutorial')}
                                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}>
                                    Start Tutorial
                                </MuiButton>
                            </Stack>
                        </Paper>
                        
                        {/* Annotation Batches Section */}
                        <Stack spacing={3}>
                            <Typography variant="h5">Available Annotation Batches</Typography>
                            
                            {batchesLoading ? (
                                <Typography>Loading batches...</Typography>
                            ) : batches.length > 0 ? (
                                <Stack spacing={2}>
                                    {batches.map((batchId) => (
                                        <Paper key={batchId} elevation={2} sx={{ p: 3 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="h6">Batch: {batchId}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Click to start annotating this batch
                                                    </Typography>
                                                </Box>
                                                <MuiButton
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => navigate(`/annotate/${batchId}`)}>
                                                    Start Annotating
                                                </MuiButton>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Alert severity="info">
                                    No annotation batches available. Create a batch using the sample data generator.
                                </Alert>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};